import fs from 'node:fs';
import path from 'node:path';
import ignore, { type Ignore } from 'ignore';

type IgnoreLayer = {
  dirRel: string;
  ig: Ignore;
};

const DEFAULT_IGNORED = ['.git', '.git/**'];

function normalizeRelPath(relPath: string): string {
  return relPath.replace(/\\/g, '/');
}

export class GitignoreFilter {
  private layers: IgnoreLayer[] = [{ dirRel: '', ig: ignore().add(DEFAULT_IGNORED) }];

  /** Load `.gitignore` in `absDir` and push a layer when the file exists. */
  async enterDirectory(absDir: string, dirRel: string): Promise<void> {
    try {
      const content = await fs.promises.readFile(path.join(absDir, '.gitignore'), 'utf8');
      this.layers.push({ dirRel, ig: ignore().add(content) });
    } catch {
      /* no .gitignore in this directory */
    }
  }

  /** Pop the ignore layer for `dirRel` when leaving that directory during a walk. */
  leaveDirectory(dirRel: string): void {
    const top = this.layers[this.layers.length - 1];
    if (top != null && top.dirRel === dirRel) {
      this.layers.pop();
    }
  }

  isIgnored(relPath: string, opts?: { directory?: boolean }): boolean {
    const normalized = normalizeRelPath(relPath);
    const checkPath =
      opts?.directory && !normalized.endsWith('/') ? `${normalized}/` : normalized;

    for (const layer of this.layers) {
      if (layer.dirRel !== '' && !isUnderDir(normalized, layer.dirRel)) continue;
      const local = layer.dirRel === '' ? checkPath : checkPath.slice(layer.dirRel.length + 1);
      if (local !== '' && layer.ig.ignores(local)) return true;
    }
    return false;
  }
}

function isUnderDir(relPath: string, dirRel: string): boolean {
  return relPath === dirRel || relPath.startsWith(`${dirRel}/`);
}

/** Build an ignore filter from root through the parent directories of `relPath`. */
export async function createGitignoreFilterForRelativePath(
  root: string,
  relPath: string,
): Promise<GitignoreFilter> {
  const filter = new GitignoreFilter();
  await filter.enterDirectory(root, '');

  const dirRel = normalizeRelPath(path.dirname(relPath));
  if (dirRel === '.' || dirRel === '') return filter;

  let abs = root;
  let rel = '';
  for (const segment of dirRel.split('/')) {
    abs = path.join(abs, segment);
    rel = rel ? `${rel}/${segment}` : segment;
    await filter.enterDirectory(abs, rel);
  }
  return filter;
}

export async function isPathGitIgnored(root: string, relPath: string): Promise<boolean> {
  const normalized = normalizeRelPath(relPath);
  const filter = await createGitignoreFilterForRelativePath(root, normalized);
  return filter.isIgnored(normalized);
}
